import { batchCheckIn, getCheckinFormConfig, listActiveEvents } from '@jq4race/api-client';
import { translateErrorCode, useTranslation } from '@jq4race/i18n';
import type {
  BatchCheckInItem,
  BatchCheckInItemResult,
  EventSummaryResponse,
} from '@jq4race/shared';
import { useCallback, useEffect, useState } from 'react';

import { PageHeader } from '../../../shell/ui/PageHeader';
import { FlashBatchQueue, type QueuedItem } from '../components/FlashBatchQueue';
import { FlashEventPicker } from '../components/FlashEventPicker';
import { FlashMatchForm } from '../components/FlashMatchForm';

function clientId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

/**
 * FlashPage — S4-UI Flash-mode operator screen (design §4). Fast repeated
 * flow: pick the active event once, then for each arrival: plate/name lookup
 * → accumulate into a batch → submit the whole batch in one call
 * (POST /events/:id/batch-checkin), surfacing per-item success/failure
 * (R4.4 — no all-or-nothing rollback). The selected event's `photoPolicy`
 * (event-photo-capture Slice B) is forwarded to `FlashMatchForm`, which gates
 * its own angle-capture UI: `deferred` renders no camera flow at all (legacy
 * behavior); `required_4`/`optional` show the 4-angle grid, capturing and
 * uploading each photo immediately (capture-at-add, D1) so the S3 object is
 * persisted upfront. Note: the DB upload token is still validated (and
 * consumed) at submit time and expires ~15 min after issue, so a very long
 * queueing session can reject early items with `photo_token_invalid` — this
 * is fail-safe (per-item, no partial commit), not silent data loss.
 */
export function FlashPage() {
  const { t } = useTranslation('flash');
  const [events, setEvents] = useState<EventSummaryResponse[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // QA-oct I9 - null = location does not expose parking_zone (or config unavailable).
  const [zoneField, setZoneField] = useState<{ required: boolean } | null>(null);

  const [queue, setQueue] = useState<QueuedItem[]>([]);
  const [itemsById, setItemsById] = useState<Record<string, BatchCheckInItem>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [results, setResults] = useState<BatchCheckInItemResult[] | null>(null);

  // Reload the active-event list from the server. Called on mount AND whenever
  // the operator returns to the picker via "Change event", so events activated
  // in the admin after this screen loaded show up without a full page reload.
  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const res = await listActiveEvents();
      setEvents(res.events);
    } catch {
      setEventsError(t('page.errors.loadEvents'));
    } finally {
      setEventsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  // QA-oct I9 - load the event location's check-in config so Flash honours the
  // parking_zone field/required flag. Best-effort: on failure the field is
  // hidden and the server (BatchCheckInUseCase) still enforces `required`,
  // reporting it per item.
  const selectedLocationId = events.find((e) => e.id === selectedEventId)?.locationId ?? null;
  useEffect(() => {
    setZoneField(null);
    if (!selectedLocationId) return;
    let cancelled = false;
    getCheckinFormConfig(selectedLocationId)
      .then((res) => {
        if (cancelled) return;
        const field = res.checkinConfig?.fields.find((f) => f.id === 'parking_zone');
        setZoneField(field ? { required: field.required } : null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  function handleAddToBatch(item: BatchCheckInItem) {
    const id = clientId();
    setQueue((prev) => [...prev, { clientId: id, plate: item.plate, ticketType: item.ticketType }]);
    setItemsById((prev) => ({ ...prev, [id]: item }));
  }

  function handleRemove(id: string) {
    setQueue((prev) => prev.filter((q) => q.clientId !== id));
    setItemsById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  async function handleSubmit() {
    if (!selectedEventId || queue.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const submittedQueue = queue;
      const items = submittedQueue
        .map((q) => itemsById[q.clientId])
        .filter((item): item is BatchCheckInItem => item !== undefined);
      const response = await batchCheckIn(selectedEventId, { items });

      // Reconcile (R4.4 + S4-UI review finding 2): drop the SUCCESSFUL items
      // from the queue, but RETAIN the FAILED ones (with their original
      // payload + error) so the operator can fix/resubmit instead of losing
      // them to a full wipe. `response.results` is index-aligned with the
      // submitted `items`/`submittedQueue`.
      const failedByClientId = new Map<string, string | undefined>();
      response.results.forEach((result, i) => {
        if (!result.success) {
          const failedItem = submittedQueue[i];
          if (failedItem) {
            failedByClientId.set(
              failedItem.clientId,
              result.error
                ? translateErrorCode(result.error.code, t, {
                    details: { field: result.error.field },
                  })
                : undefined,
            );
          }
        }
      });

      setQueue((prev) =>
        prev
          .filter((q) => failedByClientId.has(q.clientId))
          .map((q) => ({ ...q, error: failedByClientId.get(q.clientId) })),
      );
      setItemsById((prev) => {
        const next: Record<string, BatchCheckInItem> = {};
        for (const [id, item] of Object.entries(prev)) {
          if (failedByClientId.has(id)) next[id] = item;
        }
        return next;
      });

      if (failedByClientId.size > 0) {
        // Stay on the queue-editing view, now pre-populated with only the
        // failed items — ready to fix and resubmit.
        setResults(null);
      } else {
        setResults(response.results);
      }
    } catch {
      setSubmitError(t('page.errors.submitBatch'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setQueue([]);
    setItemsById({});
    setResults(null);
    setSubmitError(null);
  }

  if (!selectedEventId) {
    return (
      <div>
        <PageHeader
          eyebrow={t('page.eyebrow')}
          title={t('page.selectEventTitle')}
          description={t('page.selectEventDescription')}
        />
        <FlashEventPicker
          events={events}
          loading={eventsLoading}
          error={eventsError}
          onSelect={setSelectedEventId}
        />
      </div>
    );
  }

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={selectedEvent?.name ?? t('page.defaultTitle')}
        description={t('page.description')}
        actions={
          <button
            type="button"
            className="jq-caption text-muted-foreground underline"
            onClick={() => {
              setSelectedEventId(null);
              handleReset();
              void loadEvents();
            }}
          >
            {t('page.changeEvent')}
          </button>
        }
      />

      <FlashMatchForm
        eventId={selectedEventId}
        disabled={submitting || results !== null}
        photoPolicy={selectedEvent?.photoPolicy}
        zoneField={zoneField ?? undefined}
        onAddToBatch={handleAddToBatch}
      />

      <FlashBatchQueue
        items={queue}
        onRemove={handleRemove}
        onSubmit={() => void handleSubmit()}
        submitting={submitting}
        submitError={submitError}
        results={results}
        onReset={handleReset}
      />
    </div>
  );
}
